/** Floating itinerary dates use a UTC sort key, independent of the device timezone. */
export function animationTimestamp(date, time) {
    var _a;
    const value = String(date !== null && date !== void 0 ? date : '').trim();
    if (!value)
        return null;
    const clock = String(time !== null && time !== void 0 ? time : '').trim();
    const russianDate = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    const day = russianDate ? `${russianDate[3]}-${russianDate[2]}-${russianDate[1]}` : (_a = value.match(/^(\d{4}-\d{2}-\d{2})(?:$|[T ])/i)) === null || _a === void 0 ? void 0 : _a[1];
    if (!day)
        return null;
    const midnight = Date.parse(`${day}T00:00:00Z`);
    if (!Number.isFinite(midnight) || new Date(midnight).toISOString().slice(0, 10) !== day)
        return null;
    const dateTime = value.match(/^\d{4}-\d{2}-\d{2}[T ](\d{1,2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?)(Z|[+-]\d{2}:?\d{2})?$/i);
    if (!clock && value !== day && !russianDate && !dateTime)
        return null;
    const effectiveClock = clock || (dateTime === null || dateTime === void 0 ? void 0 : dateTime[1]) || '00:00:00';
    const parts = effectiveClock.match(/^(\d{1,2}):(\d{2})(?::(\d{2})(\.\d{1,3})?)?$/);
    if (!parts || Number(parts[1]) > 23 || Number(parts[2]) > 59 || Number(parts[3] || 0) > 59)
        return null;
    // A separate form clock overrides an embedded clock. Preserve explicit offsets
    // on complete datetimes; compare floating form values consistently everywhere.
    const zone = clock ? 'Z' : (dateTime === null || dateTime === void 0 ? void 0 : dateTime[2]) || 'Z';
    const timestamp = Date.parse(`${day}T${parts[1].padStart(2, '0')}:${parts[2]}:${parts[3] || '00'}${parts[4] || ''}${zone}`);
    return Number.isFinite(timestamp) ? timestamp : null;
}
const row = (value) => value && typeof value === 'object' ? value : {};
const filled = (value) => value !== null && value !== undefined && value !== '';
const pick = (data, keys) => keys.map(key => data[key]).find(filled);
const fields = (value) => {
    const data = row(value), raw = row(data.raw);
    return Object.assign({}, ...[row(raw.properties), raw, row(data.properties), data]
        .map(layer => Object.fromEntries(Object.entries(layer).filter(([, value]) => filled(value)))));
};
const DATE_KEYS = ['date', 'point_date', 'date_point', 'date_start', 'start_date', 'start_datetime'];
const TIME_KEYS = ['time', 'point_time', 'time_point', 'time_start', 'start_time'];
const pointDateTime = (value) => {
    var _a, _b;
    const data = fields(value), raw = row(row(value).raw);
    const original = { ...row(raw.properties), ...raw };
    // Display clocks may be shortened to HH:mm. Sort with the original seconds.
    return { date: (_a = pick(original, DATE_KEYS)) !== null && _a !== void 0 ? _a : pick(data, DATE_KEYS), time: (_b = pick(original, TIME_KEYS)) !== null && _b !== void 0 ? _b : pick(data, TIME_KEYS) };
};
export function animationPointTimestamp(value) {
    const { date, time } = pointDateTime(value);
    return animationTimestamp(date, time);
}
/** Follow linked starts and route order; arrival's local clock may precede departure. */
export function animationDeparturePoint(points) {
    var _a;
    const entries = points.map((point, index) => {
        const data = fields(point);
        const id = pick(data, ['point_travel_id', 'point_id', 'id']);
        const parent = pick(data, ['linked_start_id', 'linked_point_id']);
        const order = pick(data, ['point_order', 'order', 'sort_order']);
        return { point, index, id: String(id !== null && id !== void 0 ? id : ''), parent: String(parent !== null && parent !== void 0 ? parent : ''), order: filled(order) && Number.isFinite(Number(order)) ? Number(order) : index };
    });
    const ids = new Set(entries.map(entry => entry.id).filter(Boolean));
    const parents = new Set(entries.map(entry => entry.parent).filter(Boolean));
    const roots = entries.filter(entry => entry.id && parents.has(entry.id) && (!ids.has(entry.parent) || entry.id === entry.parent));
    return (_a = (roots.length ? roots : entries).sort((a, b) => a.order - b.order || a.index - b.index)[0]) === null || _a === void 0 ? void 0 : _a.point;
}
export function animationDepartureTimestamp(points) {
    return animationPointTimestamp(animationDeparturePoint(points));
}
/** General-map movements expose start, points, or start_datetime. Never use finish. */
export function animationMovementTimestamp(value) {
    var _a, _b, _c;
    const data = fields(value);
    const candidates = [data.start, animationDeparturePoint(Array.isArray(data.points) ? data.points : [])];
    for (const point of candidates) {
        const { date, time } = pointDateTime(point);
        if (filled(time) || /[T ]\d{1,2}:\d{2}/.test(String(date !== null && date !== void 0 ? date : ''))) {
            const timestamp = animationTimestamp(date, time);
            if (timestamp !== null)
                return timestamp;
        }
    }
    const date = pick(data, ['date_start', 'start_date', 'date']), time = pick(data, ['time_start', 'start_time', 'time']);
    const metadataTimestamp = animationTimestamp(date, time);
    return (_c = (_b = (_a = animationTimestamp(data.start_datetime)) !== null && _a !== void 0 ? _a : (filled(time) || /[T ]\d{1,2}:\d{2}/.test(String(date !== null && date !== void 0 ? date : '')) ? metadataTimestamp : null)) !== null && _b !== void 0 ? _b : candidates.map(animationPointTimestamp).find(timestamp => timestamp !== null)) !== null && _c !== void 0 ? _c : metadataTimestamp;
}
